import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../services/api";
import { getProcurementOrder } from "../services/procurementService";
import {
  approveSupplierInvoice,
  getSupplierInvoice,
  matchSupplierInvoiceLines,
  recomputeSupplierInvoiceDifferences,
  rejectSupplierInvoice,
} from "../services/supplierInvoiceService";
import SupplierInvoiceControlStatusBadge from "../components/supplier-invoices/SupplierInvoiceControlStatusBadge";
import SupplierInvoiceSeverityBadge from "../components/supplier-invoices/SupplierInvoiceSeverityBadge";

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("nl-NL");
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL");
}

function formatMoney(value) {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function shortId(value) {
  const text = String(value || "");
  if (!text) return "-";
  return text.slice(-8);
}

function lineMatchValue(line) {
  return String(line?.procurementOrderLineId || "");
}

function differenceText(line) {
  const type = String(line?.differenceType || "none");
  if (type === "none") return "Geen verschil";
  if (type === "price_delta") return "Prijsafwijking";
  if (type === "quantity_delta") return "Aantalafwijking";
  if (type === "unknown_charge") return "Onbekende toeslag";
  if (type === "rounding") return "Afrondingsverschil";
  return type;
}

export default function SupplierInvoiceDetailPage() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState("");
  const [invoice, setInvoice] = useState(null);
  const [procurementOrder, setProcurementOrder] = useState(null);
  const [supplierName, setSupplierName] = useState("");
  const [matchDrafts, setMatchDrafts] = useState({});
  const [matchNote, setMatchNote] = useState("");
  const [decisionNote, setDecisionNote] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const invoiceRes = await getSupplierInvoice(id);
      setInvoice(invoiceRes || null);

      const tasks = [];
      if (invoiceRes?.procurementOrderId) {
        tasks.push(
          getProcurementOrder(invoiceRes.procurementOrderId)
            .then((order) => setProcurementOrder(order || null))
            .catch(() => setProcurementOrder(null))
        );
      } else {
        setProcurementOrder(null);
      }

      if (invoiceRes?.supplierOrganizationId) {
        tasks.push(
          api
            .get(`/organizations/${invoiceRes.supplierOrganizationId}`)
            .then((res) => {
              const data = res?.data || {};
              setSupplierName(data.displayName || data.name || data.email || "");
            })
            .catch(() => setSupplierName(""))
        );
      } else {
        setSupplierName("");
      }

      await Promise.all(tasks);
      setMatchDrafts(
        (Array.isArray(invoiceRes?.lines) ? invoiceRes.lines : []).reduce((acc, line) => {
          acc[String(line._id)] = lineMatchValue(line);
          return acc;
        }, {})
      );
    } catch (err) {
      setError(err?.response?.data?.message || "Factuur laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const orderLines = Array.isArray(procurementOrder?.lines) ? procurementOrder.lines : [];

  const pendingMatches = useMemo(() => {
    if (!Array.isArray(invoice?.lines)) return [];
    return invoice.lines
      .map((line) => ({
        supplierInvoiceLineId: String(line._id),
        procurementOrderLineId: String(matchDrafts[String(line._id)] || ""),
      }))
      .filter((item) => item.procurementOrderLineId);
  }, [invoice, matchDrafts]);

  const recompute = async () => {
    try {
      setActionBusy(true);
      setError("");
      await recomputeSupplierInvoiceDifferences(id, { note: "Handmatig herberekend vanuit detailpagina" });
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Verschillen opnieuw berekenen mislukt.");
    } finally {
      setActionBusy(false);
    }
  };

  const submitMatches = async () => {
    if (!pendingMatches.length) {
      setError("Kies minimaal 1 inkoopregel om te koppelen.");
      return;
    }
    try {
      setActionBusy(true);
      setError("");
      await matchSupplierInvoiceLines(id, {
        matches: pendingMatches,
        note: matchNote,
      });
      setMatchNote("");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Regelkoppeling mislukt.");
    } finally {
      setActionBusy(false);
    }
  };

  const approve = async () => {
    try {
      setActionBusy(true);
      setError("");
      await approveSupplierInvoice(id, { note: decisionNote });
      setDecisionNote("");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Goedkeuren mislukt.");
    } finally {
      setActionBusy(false);
    }
  };

  const reject = async () => {
    if (!String(rejectReason || "").trim()) {
      setError("Afwijsreden is verplicht.");
      return;
    }
    try {
      setActionBusy(true);
      setError("");
      await rejectSupplierInvoice(id, { reason: rejectReason });
      setRejectReason("");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Afwijzen mislukt.");
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) {
    return <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">Factuur laden...</div>;
  }

  if (error && !invoice) {
    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>
        <Link to="/pl/inkoop/facturen" className="inline-flex rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
          Terug naar factuurcontrole
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Leveranciersfactuur</p>
            <h1 className="text-2xl font-semibold text-slate-900">{invoice?.invoiceNumber || "-"}</h1>
            <p className="text-sm text-slate-600">
              {supplierName || shortId(invoice?.supplierOrganizationId)} · {formatDate(invoice?.invoiceDate)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/pl/inkoop/facturen" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
              Terug
            </Link>
            <button
              type="button"
              onClick={recompute}
              disabled={actionBusy}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 disabled:opacity-60"
            >
              Verschillen herberekenen
            </button>
          </div>
        </div>
      </header>

      {error && <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}

      {invoice && (
        <>
          <section className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Factuurkop</h2>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <p className="text-sm text-slate-700"><span className="text-slate-500">Leverancier:</span> {supplierName || shortId(invoice.supplierOrganizationId)}</p>
                <p className="text-sm text-slate-700"><span className="text-slate-500">Factuurdatum:</span> {formatDate(invoice.invoiceDate)}</p>
                <p className="text-sm text-slate-700"><span className="text-slate-500">Status:</span> {invoice.status || "-"}</p>
                <p className="text-sm text-slate-700"><span className="text-slate-500">Controlestatus:</span> <SupplierInvoiceControlStatusBadge status={invoice.controlStatus} /></p>
                <p className="text-sm text-slate-700"><span className="text-slate-500">Totaal:</span> {formatMoney(invoice.totalAmount)}</p>
                <p className="text-sm text-slate-700"><span className="text-slate-500">Valuta:</span> {invoice.currency || "EUR"}</p>
                <p className="text-sm text-slate-700"><span className="text-slate-500">Project:</span> {invoice.projectId ? shortId(invoice.projectId) : "-"}</p>
                <p className="text-sm text-slate-700"><span className="text-slate-500">Werkbon:</span> {invoice.workorderId ? shortId(invoice.workorderId) : "-"}</p>
                <p className="text-sm text-slate-700"><span className="text-slate-500">Document:</span> {invoice.incomingDocumentId ? shortId(invoice.incomingDocumentId) : "-"}</p>
                <p className="text-sm text-slate-700"><span className="text-slate-500">Beslissing:</span> {invoice.controllerDecision?.status || "-"}</p>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Afwijkingen</h2>
              <div className="mt-4 space-y-2 text-sm text-slate-700">
                <p><span className="text-slate-500">Severity:</span> <SupplierInvoiceSeverityBadge severity={invoice?.differenceSummary?.severity} /></p>
                <p><span className="text-slate-500">Totaal verschil:</span> {formatMoney(invoice?.differenceSummary?.lineDifferenceTotal || 0)}</p>
                <p><span className="text-slate-500">Extra toeslagen:</span> {formatMoney(invoice?.differenceSummary?.extraChargeTotal || 0)}</p>
                <p><span className="text-slate-500">Niet gekoppelde regels:</span> {Number(invoice?.differenceSummary?.missingMatchCount || 0)}</p>
                <p><span className="text-slate-500">Binnen tolerantie:</span> {invoice?.differenceSummary?.withinTolerance ? "Ja" : "Nee"}</p>
                <p><span className="text-slate-500">Laatst berekend:</span> {formatDateTime(invoice?.differenceSummary?.lastComputedAt)}</p>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Gekoppelde inkooporder</h2>
                <p className="mt-1 text-sm text-slate-700">
                  {procurementOrder
                    ? `${procurementOrder.orderNumber || procurementOrder._id} · ${procurementOrder.title || "-"}`
                    : invoice.procurementOrderId
                      ? shortId(invoice.procurementOrderId)
                      : "Nog niet gekoppeld"}
                </p>
              </div>
              {procurementOrder?._id && (
                <Link to={`/pl/inkoop/${procurementOrder._id}`} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                  Open inkooporder
                </Link>
              )}
            </div>
            {procurementOrder && (
              <div className="mt-4 grid gap-3 md:grid-cols-3 text-sm text-slate-700">
                <p><span className="text-slate-500">Leverancier:</span> {procurementOrder.supplierName || "-"}</p>
                <p><span className="text-slate-500">Project:</span> {procurementOrder.projectName || "-"}</p>
                <p><span className="text-slate-500">Werkbon:</span> {procurementOrder.workOrderTitle || "-"}</p>
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Regelcontrole</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Koppel factuurregels handmatig aan regels uit de gekoppelde inkooporder.
                </p>
              </div>
              <button
                type="button"
                onClick={submitMatches}
                disabled={actionBusy || !procurementOrder || pendingMatches.length === 0}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 disabled:opacity-60"
              >
                Geselecteerde regels koppelen
              </button>
            </div>

            <div className="mt-3">
              <input
                value={matchNote}
                onChange={(e) => setMatchNote(e.target.value)}
                placeholder="Notitie bij koppelen (optioneel)"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Factuurregel</th>
                    <th className="px-4 py-3 font-medium">Bedrag</th>
                    <th className="px-4 py-3 font-medium">Gekoppelde inkoopregel</th>
                    <th className="px-4 py-3 font-medium">Afwijking</th>
                    <th className="px-4 py-3 font-medium">Handmatig koppelen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(Array.isArray(invoice.lines) ? invoice.lines : []).map((line) => (
                    <tr key={line._id}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{line.description || "-"}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          {line.quantity || 0} {line.unit || ""} · regel {line.lineNumber || "-"}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{formatMoney(line.lineTotal)}</td>
                      <td className="px-4 py-3 text-slate-700">
                        {line.procurementOrderLineId
                          ? shortId(line.procurementOrderLineId)
                          : "Nog niet gekoppeld"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <SupplierInvoiceSeverityBadge severity={Number(line?.differenceAmount || 0) <= 2 ? "green" : Number(line?.differenceAmount || 0) <= 50 ? "yellow" : "red"} />
                          <span className="text-xs text-slate-500">{differenceText(line)}</span>
                          <span className="text-xs text-slate-500">{formatMoney(line.differenceAmount || 0)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={String(matchDrafts[String(line._id)] || "")}
                          onChange={(e) =>
                            setMatchDrafts((prev) => ({
                              ...prev,
                              [String(line._id)]: e.target.value,
                            }))
                          }
                          disabled={!procurementOrder}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-100"
                        >
                          <option value="">Niet koppelen</option>
                          {orderLines.map((orderLine) => (
                            <option key={orderLine._id} value={orderLine._id}>
                              {(orderLine.description || orderLine.title || "Regel").slice(0, 80)} · {formatMoney(orderLine.lineTotal || orderLine.totalPrice || 0)}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Goedkeuren</h2>
              <p className="mt-1 text-sm text-slate-600">
                Leg een korte controllernotitie vast en keur de factuur goed.
              </p>
              <textarea
                value={decisionNote}
                onChange={(e) => setDecisionNote(e.target.value)}
                rows={4}
                className="mt-4 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="Notitie bij goedkeuring"
              />
              <button
                type="button"
                onClick={approve}
                disabled={actionBusy}
                className="mt-3 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                Factuur goedkeuren
              </button>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Afwijzen</h2>
              <p className="mt-1 text-sm text-slate-600">
                Afwijzen vereist een reden; deze wordt auditbaar opgeslagen.
              </p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                className="mt-4 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="Reden van afwijzen"
              />
              <button
                type="button"
                onClick={reject}
                disabled={actionBusy}
                className="mt-3 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              >
                Factuur afwijzen
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
